import { Link } from 'react-router-dom';
import { PageShell } from '../components/PageShell';
import { SUPPORT_EMAIL } from '../content';

const UPDATED = '12 March 2025';

export function TermsPage() {
  return (
    <PageShell>
      <div className="container">
        <div className="prose legal-head">
          <div className="eyebrow" style={{ marginBottom: 22 }}>Legal · Terms</div>
          <h1 className="h-display" style={{ fontSize: 'clamp(38px, 5.4vw, 64px)' }}>
            Terms of Service
          </h1>
          <p className="h-sub" style={{ marginTop: 20 }}>
            These terms are the agreement between you and Cranio Studio for using the app and this
            site. We&apos;ve kept them plain. By downloading or using Cranio, you agree to what&apos;s
            below.
          </p>
          <div
            className="mono"
            style={{
              display: 'flex',
              gap: 24,
              flexWrap: 'wrap',
              marginTop: 24,
              fontSize: 11,
              letterSpacing: '0.06em',
              color: 'var(--fg-3)',
            }}
          >
            <span>Last updated · {UPDATED}</span>
            <span>Effective · {UPDATED}</span>
          </div>
        </div>

        <div className="legal-layout">
          <nav className="toc">
            <h4>On this page</h4>
            <ol>
              <li><a href="#agreement"><span className="n">01</span> The agreement</a></li>
              <li><a href="#license"><span className="n">02</span> Your license</a></li>
              <li><a href="#content"><span className="n">03</span> Your work</a></li>
              <li><a href="#pro"><span className="n">04</span> Cranio Pro</a></li>
              <li><a href="#billing"><span className="n">05</span> Billing &amp; renewal</a></li>
              <li><a href="#conduct"><span className="n">06</span> Acceptable use</a></li>
              <li><a href="#ip"><span className="n">07</span> Our IP</a></li>
              <li><a href="#warranty"><span className="n">08</span> Warranty</a></li>
              <li><a href="#liability"><span className="n">09</span> Liability</a></li>
              <li><a href="#termination"><span className="n">10</span> Termination</a></li>
              <li><a href="#law"><span className="n">11</span> Governing law</a></li>
              <li><a href="#contact"><span className="n">12</span> Contact</a></li>
            </ol>
          </nav>

          <article className="prose">
            <p style={{ fontSize: '15.5px', color: 'var(--fg-1)' }} id="agreement">
              <strong>01 · The agreement.</strong> &ldquo;Cranio,&rdquo; &ldquo;we,&rdquo; or
              &ldquo;us&rdquo; means Cranio Studio. &ldquo;You&rdquo; means the person using the
              app. These Terms, together with our{' '}
              <Link className="inline" to="/privacy">Privacy Policy</Link>, govern your use of the
              Cranio app and website. If you don&apos;t agree, please don&apos;t use Cranio. Purchases
              made through an app store are also subject to Apple&apos;s or Google&apos;s standard
              terms.
            </p>

            <h2 id="license"><span className="num">02</span> Your license to use Cranio</h2>
            <p>
              We grant you a personal, non-exclusive, non-transferable, revocable license to install
              and use Cranio on devices you own or control, for your own creative practice — whether
              that&apos;s hobby, study, or professional work. This license is for the app itself;
              it doesn&apos;t transfer ownership of the software.
            </p>

            <h2 id="content"><span className="num">03</span> Your work belongs to you</h2>
            <p>
              Anything you create with Cranio — saved poses, lighting setups and exported reference
              shots — is yours. We claim no ownership over your output and don&apos;t access it.
              You&apos;re free to use studies you produce in Cranio for any lawful purpose, including
              commercial illustration and teaching.
            </p>

            <h2 id="pro"><span className="num">04</span> Cranio Pro</h2>
            <p>
              Cranio is free to download with the planar head and core studio tools. Cranio Pro
              unlocks every head model, all surface finishes, pro lighting rigs and 4K export. Pro
              is offered as an annual subscription or a one-time lifetime
              purchase, at the prices shown in the app.
            </p>

            <h2 id="billing"><span className="num">05</span> Billing, renewal &amp; refunds</h2>
            <ul className="bullets">
              <li>Subscriptions are billed through your Apple Account or Google Account and <strong>renew automatically</strong> unless cancelled at least 24 hours before the period ends.</li>
              <li>Manage or cancel anytime in <strong>iOS Settings → Subscriptions</strong> or <strong>Google Play → Subscriptions</strong>.</li>
              <li>Refunds are handled by Apple or Google under their store policies. We&apos;re glad to help — but we can&apos;t issue store refunds directly.</li>
            </ul>

            <h2 id="conduct"><span className="num">06</span> Acceptable use</h2>
            <p>You agree not to:</p>
            <ul className="bullets">
              <li>Copy, decompile, reverse-engineer or resell the app, except where the law expressly allows it.</li>
              <li>Circumvent the Pro purchase flow or licensing checks.</li>
              <li>Use Cranio to break the law or infringe someone else&apos;s rights.</li>
            </ul>

            <h2 id="ip"><span className="num">07</span> Our intellectual property</h2>
            <p>
              The Cranio app, its name, logo, the studio interface, the head models, materials and
              all related design are owned by Cranio Studio and protected by intellectual-property
              law. The license in Section 2 doesn&apos;t give you any right to our branding or
              assets beyond using the app as intended.
            </p>

            <h2 id="warranty"><span className="num">08</span> Warranty disclaimer</h2>
            <p>
              Cranio is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; To the fullest
              extent permitted by law, we disclaim all warranties, express or implied, including
              merchantability, fitness for a particular purpose and non-infringement. We don&apos;t
              promise the app will be uninterrupted, error-free, or that every model will render
              identically on every device.
            </p>

            <h2 id="liability"><span className="num">09</span> Limitation of liability</h2>
            <p>
              To the extent the law allows, Cranio Studio will not be liable for indirect,
              incidental, special or consequential damages, or lost data or profits, arising from
              your use of the app. Where liability can&apos;t be excluded, it is limited to the
              amount you paid us in the twelve months before the claim.
            </p>

            <h2 id="termination"><span className="num">10</span> Termination</h2>
            <p>
              You can stop using Cranio and delete it at any time. We may suspend or end your
              license if you materially breach these Terms. Sections that by their nature should
              survive — ownership, disclaimers, liability — continue after termination.
            </p>

            <h2 id="law"><span className="num">11</span> Governing law &amp; changes</h2>
            <p>
              These Terms are governed by the laws of the jurisdiction in which Cranio Studio is
              established, without regard to conflict-of-laws rules. We may update these Terms
              from time to time; we&apos;ll revise the date above and, for material changes, give
              notice in the app or on this site. Continued use means you accept the updated Terms.
            </p>

            <h2 id="contact"><span className="num">12</span> Contact us</h2>
            <p>
              Questions about these Terms? Email{' '}
              <a
                className="inline"
                href={`mailto:${SUPPORT_EMAIL}?subject=Terms%20question`}
              >
                {SUPPORT_EMAIL}
              </a>{' '}
              or visit our <Link className="inline" to="/contact">contact page</Link>.
            </p>
          </article>
        </div>
      </div>
    </PageShell>
  );
}
